<template>
<div>
	<v-select
		placeholder="Search for a constructor"
		:options="options"
		@search="fetchConstructors"
		:filterable="false"
		@input="fetchSolves"
		>
		<template #search="{ attributes, events }">
			<input
        class="vs__search browser-default"
        v-bind="attributes"
        v-on="events"
				/>
		</template>
		<template #no-options="{ searching }">
			<span v-if="searching">No constructors found with that name</span>
			<span v-else>Search for a constructor using any part of their name</span>
		</template>
	</v-select>
	<div class="const-meta" v-if="solveMeta" :class="{faster: !metaIsSlower, slower: metaIsSlower}">
		<div class="card">
			<div class="meta-val">{{ solveMeta.count }}</div>
			<div class="meta-label">Total Solves</div>
		</div>
		<div class="card">
			<div class="meta-val" v-html="formatToAverage(solveMeta.aboveAvg)"></div>
			<div class="meta-label">
				<span class="hide-on-small-and-down">{{ metaIsSlower ? 'slower' : 'faster' }} than average</span>
				<span class="hide-on-med-and-up">{{ metaIsSlower ? 'slower' : 'faster' }} vs avg</span>
			</div>
		</div>
		<div v-if="solveMeta.rank > 0" class="card">
			<div class="meta-val colored-faster">#{{ solveMeta.rank }}</div>
			<div class="meta-label">{{ metaIsSlower ? 'slowest' : 'fastest' }} <span class="hide-on-small-and-down">constructor </span>(of {{ solveMeta.totalConstructors }})</div>
		</div>
		<div v-else class="card">
			<div class="meta-val">N/A</div>
			<div class="meta-label">(not enough solves to rank)</div>
		</div>
	</div>
	<AgGridVue
		style="width: 100%"
		class="ag-theme-material"
		:columnDefs="solveTableColumns"
		:rowData="solves"
		domLayout='autoHeight'
		@grid-ready="onGridReady"
		@model-updated="resizeGrid"
		:overlayNoRowsTemplate="customTableNoRowsMessage"
	/>
</div>
</template>
<script>
const axios = require('axios');
const cookies = require('browser-cookies');
const debounce = require('lodash.debounce');
import { AgGridVue } from 'ag-grid-vue';
import { parse, format } from 'date-fns';
import { SHORT_DAY_MAPPING, DAY_VAL_MAPPING } from '../utils/dates';

export default {
	name: 'ConstructorSearch',
	components: {
		AgGridVue,
	},
	props: {
	},
	data() {
		return {
			options: [],
			solves: [],
			solveMeta: null,
			gridApi: null,
			columnApi: null,
			solveTableColumns: [
				{
					field: 'solved',
					headerName: 'Solved',
					resizable: true,
					cellRenderer: params => {
						if (params.value) {
							return '<i class="amber-text text-lighten-2 fas fa-star"></i>';
						} else {
							return '<i class="blue-text text-lighten-2 fas fa-star"></i>';
						}
					},
					cellStyle: {
						justifyContent: 'center',
					},
				},
				{
					headerName: 'Puzzle',
					sortable: true,
					valueGetter: params => {
						return params.data.date;
					},
					cellRenderer: params => {
						const parsed = parse(params.value, 'yyyy-MM-dd', new Date());
						return '<a href="/puzzles/' + params.value + '">' + format(parsed, 'MMM d, yyyy') + '</a>';
					},
				},
				{
					headerName: 'Day of Week',
					valueGetter: params => {
						return SHORT_DAY_MAPPING[params.data.dow];
					},
					comparator: (v1, v2) => {
						v1 = DAY_VAL_MAPPING[v1]
						v2 = DAY_VAL_MAPPING[v2]
						if (v1 === v2) return 0
						return v1 > v2 ? 1 : -1
					},
					sortable: true,
				},
				{
					field: 'time',
					headerName: 'Solve Time',
					valueFormatter: params => {
						return this.$options.filters.prettyTime(params.value);
					},
					sortable: true,
				},
				{
					field: 'adjustedTime',
					headerName: 'Adjusted Time',
					valueFormatter: params => {
						return this.$options.filters.prettyTime(params.value);
					},
					sortable: true,
				},
				{
					field: 'aboveAvg',
					headerName: 'To Average',
					cellRenderer: params => {
						return this.formatToAverage(params.value);
					},
					sortable: true,
				},
				{
					field: 'constructor',
					headerName: 'Constructor',
					sortable: true,
				},
			],
			customTableNoRowsMessage: '<span class="ag-overlay-no-rows-center">No puzzle solves to display</span>',
		};
	},
	computed: {
		metaIsSlower: function() {
			return this.solveMeta && this.solveMeta.aboveAvg > 0;
		},
	},
	methods: {
		fetchConstructors: function(search, loading) {
			if (search.length >= 3) {
				loading(true);
				this.search(search, loading, this);
			}
		},
		search: debounce((search, loading, ctx) => {
			return axios({
				method: 'get',
				url: '/api/constructors',
				headers: {
					'X-CSRFToken': cookies.get('csrftoken'),
				},
				params: {
					search: search
				},
			}).then(resp => {
				ctx.options = resp.data.results;
			}).finally(() => {
				loading(false);
			});
		}, 300),
		fetchSolves: function(constructor) {
			if (!constructor) {
				this.solves = [];
				this.solveMeta = null;
				return;
			}
			return axios({
				method: 'get',
				url: '/api/constructors',
				headers: {
					'X-CSRFToken': cookies.get('csrftoken'),
				},
				params: {
					solves: constructor,
				},
			}).then(resp => {
				this.solves = resp.data.solves;
				if (resp.data.meta.count > 0) {
					this.solveMeta = resp.data.meta;
				} else {
					this.solveMeta = null;
				}
				this.columnApi.autoSizeAllColumns();
			});
		},
		onGridReady(params) {
			this.gridApi = params.api;
			this.columnApi = params.columnApi;
			this.resizeGrid();
		},
		resizeGrid() {
			if (this.columnApi) {
				this.columnApi.autoSizeAllColumns();
			}
		},
		formatToAverage(value) {
			if (!value) {
				return "";
			}
			const good = value <= 0;
			const pct = Math.abs(value * 100).toFixed(1);
			return `<span class="${good ? 'green-text text-darken-1' : 'red-text text-darken-1'}"><i class="fas ${good ? 'fa-arrow-down' : 'fa-arrow-up'}"></i> ${pct}%</span>`;
		},
	},
};
</script>
<style lang="scss">
  @import "../../node_modules/ag-grid-community/dist/styles/ag-grid.css";
  @import "../../node_modules/ag-grid-community/dist/styles/ag-theme-material.css";
	.vs__search::placeholder {
		color: #a8a8a8;
	}
	.vs__dropdown-toggle {
		padding-top: 10px;
		padding-bottom: 14px;
	}
	.ag-row .ag-cell {
		display: flex;
		align-items: center; /* vertically center all cell content */
		font-family: 'Libre Franklin', sans-serif;
	}
</style>
